import { performedSetSchema } from '@cadencia/contracts';
import { z } from 'zod';
import { campoDe, camposConProblema, esErrorDeValidacion, problemasDe } from './errores.js';

// Detectar un error de validacion se hace en UN sitio (`errores.ts`). Aqui
// habia una copia; dos copias de la misma comprobacion acaban divergiendo sin
// que nada avise, que es lo que este repositorio lleva documentado desde la
// Fase 1. Se reexportan porque `SesionActivaPage` ya las importaba de aqui.
export { esErrorDeValidacion, camposConProblema };

/**
 * Validar una serie antes de guardarla, y contarlo en castellano (UX-001).
 *
 * Hasta el 06/09/2026 la pantalla de entrenamiento hacia `setError(error.message)`
 * con lo que devolviera `performedSetSchema.parse`. Un RIR de 30 se veia asi,
 * literalmente, en mitad del gimnasio:
 *
 *   [
 *     {
 *       "origin": "number",
 *       "code": "too_big",
 *       "maximum": 10,
 *       ...
 *
 * El contrato hacia lo correcto: rechazar el dato. Lo que fallaba era la
 * traduccion. Y el sitio donde fallaba es el de mayor friccion de toda la
 * aplicacion, con el movil sudado y el descanso corriendo.
 *
 * Aqui NO se reimplementan los limites. Los tres campos que teclea el usuario
 * se toman del propio contrato (`performedSetSchema.shape`), asi que el rango
 * sigue viviendo en un solo sitio y esta comprobacion no puede desincronizarse
 * de la que manda. Esto es una capa de PRESENTACION delante del contrato, no un
 * sustituto suyo: `logPerformedSet` sigue haciendo su `parse` y sigue siendo la
 * autoridad final.
 */

/**
 * Limites para los topes de los botones +/- y para los atributos del campo.
 *
 * Son ayuda de interfaz, no seguridad: un campo de texto no impide teclear 30.
 * Que coincidan con el contrato lo comprueba `validacionSerie.test.ts` pasando
 * los bordes por `performedSetSchema`, no leyendolos de aqui.
 */
export const LIMITES_SERIE = {
  carga: { min: 0, max: 1000, paso: 2.5 },
  reps: { min: 1, max: 100, paso: 1 },
  rir: { min: 0, max: 10, paso: 0.5 },
} as const;

/** Solo los tres campos que se teclean. El resto los rellena el repositorio. */
const camposTecleados = z.object({
  loadKg: performedSetSchema.shape.loadKg,
  reps: performedSetSchema.shape.reps,
  rir: performedSetSchema.shape.rir,
});

/**
 * Frases completas por campo, no plantillas.
 *
 * En castellano el genero y el numero cambian la frase entera —«no es valida»,
 * «no son validas», «no es valido»—, asi que una plantilla generica produce
 * texto mal escrito. Son tres campos: se escriben los tres.
 */
const MENSAJES = {
  loadKg: {
    rango: `La carga debe estar entre ${String(LIMITES_SERIE.carga.min)} y ${String(LIMITES_SERIE.carga.max)} kg.`,
    entero: 'La carga debe ser un número entero.',
    otro: 'La carga no es válida.',
  },
  reps: {
    rango: `Las repeticiones deben estar entre ${String(LIMITES_SERIE.reps.min)} y ${String(LIMITES_SERIE.reps.max)}.`,
    entero: 'Las repeticiones deben ser un número entero.',
    otro: 'Las repeticiones no son válidas.',
  },
  rir: {
    rango: `El RIR debe estar entre ${String(LIMITES_SERIE.rir.min)} y ${String(LIMITES_SERIE.rir.max)}.`,
    entero: 'El RIR debe ser un número entero.',
    otro: 'El RIR no es válido.',
  },
} as const;

/**
 * Cualquier otro campo del contrato que falle.
 *
 * No se nombra: `setPrescriptionId` o `e1rmKg` no le dicen nada a nadie, y el
 * encargo prohibe expresamente sacar nombres internos del esquema a la pantalla.
 * El detalle tecnico va al registro, que es donde sirve.
 */
const MENSAJE_GENERICO = 'Hay algún dato de la serie que no encaja. Revisa carga, repeticiones y RIR.';

function frase(problema: { code?: unknown; expected?: unknown; path?: unknown }): string {
  const campo = campoDe(problema);
  if (campo === null || !(campo in MENSAJES)) return MENSAJE_GENERICO;
  const textos = MENSAJES[campo as keyof typeof MENSAJES];

  if (problema.code === 'too_big' || problema.code === 'too_small') return textos.rango;
  // Un decimal donde se espera un entero llega como `invalid_type` con
  // `expected: 'int'`, no como un codigo propio.
  if (problema.expected === 'int') return textos.entero;
  return textos.otro;
}

/**
 * Traduce un fallo de validacion a una frase que se pueda leer.
 *
 * Devuelve `null` si el error NO es de validacion. Eso importa: un fallo al
 * escribir en Dexie no es una entrada invalida, y taparlo con «entrada
 * invalida» manda al usuario a corregir un numero que estaba bien.
 */
export function traducirErrorDeValidacion(error: unknown): string | null {
  const problemas = problemasDe(error);
  if (problemas === null) return null;

  // Una sola frase, y la del primer campo que el usuario PUEDE arreglar. Tres
  // avisos a la vez en un movil, entre series, no se leen; y si en el mismo
  // error viene un problema de una columna de sincronizacion, contar ese
  // manda a mirar donde no se puede hacer nada.
  const delUsuario = problemas.find((problema) => {
    const campo = campoDe(problema);
    return campo !== null && campo in MENSAJES;
  });
  const elegido = delUsuario ?? problemas[0];
  return elegido === undefined ? MENSAJE_GENERICO : frase(elegido);
}

export type ResultadoValidacion =
  | { ok: true; loadKg: number; reps: number; rir: number }
  | { ok: false; mensaje: string; campos: string[] };

/**
 * Comprueba los tres numeros contra el contrato ANTES de intentar guardarlos.
 *
 * Asi el caso normal —RIR 12 de un dedo torpe— se resuelve sin tocar Dexie y
 * sin generar un error que haya que interpretar. Lo que llega al repositorio ya
 * es valido, y su `parse` sigue estando ahi para lo que se cuele por otro lado.
 */
export function validarSerie(valores: {
  loadKg: number;
  reps: number;
  rir: number;
}): ResultadoValidacion {
  const salida = camposTecleados.safeParse(valores);
  if (salida.success) {
    return { ok: true, loadKg: salida.data.loadKg, reps: salida.data.reps, rir: salida.data.rir };
  }
  return {
    ok: false,
    mensaje: traducirErrorDeValidacion(salida.error) ?? MENSAJE_GENERICO,
    campos: camposConProblema(salida.error),
  };
}
